import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, MessageCircle } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const EMPTY = { student_id: "", name: "", phone: "", course: "", batch: "", photo: "", status: "Active", valid_until: "" };
const BATCHES = ["Regular Morning", "Evening Batch", "Weekend Batch"];
const STATUSES = ["Active", "Completed", "On Hold"];

function waLink(s) {
  let digits = (s.phone || "").replace(/\D/g, "");
  if (digits.length === 10) digits = `91${digits}`;
  const msg = `Dear ${s.name}, welcome to St. Mary's Charitable Trust! Your Student ID is *${s.student_id}*. View your digital ID card anytime at ${window.location.origin}/student-login — sign in with your Student ID, your full name (${s.name}) and this mobile number. God bless your learning journey.`;
  return `https://wa.me/${digits}?text=${encodeURIComponent(msg)}`;
}

function initials(name = "") {
  return name.split(" ").filter(Boolean).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
}

export default function StudentsTab() {
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/students").then((r) => setStudents(r.data)).catch((e) => toast.error(formatApiError(e, "Failed to load students.")));
    api.get("/admin/courses").then((r) => setCourses(r.data)).catch(() => {});
  }, []);

  useEffect(() => load(), [load]);

  const save = async () => {
    const { mode, data } = dialog;
    if (!data.student_id.trim() || !data.name.trim() || !data.phone.trim()) {
      toast.error("Student ID, name and phone are required.");
      return;
    }
    setSaving(true);
    try {
      if (mode === "add") {
        const r = await api.post("/admin/students", data);
        setStudents((list) => [...list, r.data]);
        if (r.data.whatsapp === "queued") {
          toast.success("Student enrolled — Student ID is being sent to their WhatsApp automatically", { duration: 8000 });
        } else {
          toast.success("Student enrolled", {
            action: { label: "Send ID on WhatsApp", onClick: () => window.open(waLink(r.data), "_blank") },
            duration: 10000,
          });
        }
      } else {
        await api.put(`/admin/students/${data.id}`, data);
        setStudents((list) => list.map((s) => (s.id === data.id ? data : s)));
        toast.success("Student updated");
      }
      setDialog(null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/students/${id}`);
      setStudents((list) => list.filter((s) => s.id !== id));
      toast.success("Student removed");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const selectCls = "mt-1.5 flex h-10 w-full rounded-md border border-input bg-white px-3 text-sm";

  return (
    <div data-testid="students-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-stone-500">
          Enrolled students can log in on the website with their Student ID, name and mobile to view their digital ID card.
        </p>
        <Button onClick={() => setDialog({ mode: "add", data: { ...EMPTY } })} data-testid="add-student-button" className="rounded-full bg-forest font-display text-cream hover:bg-forest-deep">
          <Plus className="mr-2 h-4 w-4" /> Enrol Student
        </Button>
      </div>

      <div className="mt-4 space-y-3" data-testid="students-list">
        {students.map((s) => (
          <div key={s.id} data-testid={`admin-student-row-${s.id}`} className="flex flex-col gap-4 rounded-2xl border border-forest/10 bg-white p-5 sm:flex-row sm:items-center">
            {s.photo ? (
              <img src={s.photo} alt={s.name} className="h-14 w-14 shrink-0 rounded-full object-cover" />
            ) : (
              <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-forest/10 font-serif text-xl font-bold text-forest">
                {initials(s.name)}
              </span>
            )}
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-display font-semibold text-forest-deep">{s.name}</p>
                <span className="rounded-full bg-linen px-3 py-0.5 font-mono text-[11px] text-forest">{s.student_id}</span>
                <span className={`rounded-full px-3 py-0.5 text-[11px] font-semibold ${s.status === "Active" ? "bg-forest/10 text-forest" : "bg-stone-200 text-stone-600"}`}>{s.status}</span>
              </div>
              <p className="mt-1 text-sm text-stone-500">
                {s.course || "—"} · {s.batch || "—"} · {s.phone}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <a href={waLink(s)} target="_blank" rel="noreferrer" data-testid={`student-whatsapp-${s.id}`} className="rounded-full border border-[#1FA855]/40 p-2 text-[#1FA855] hover:bg-[#1FA855] hover:text-white" aria-label="Send Student ID on WhatsApp" title="Send Student ID on WhatsApp">
                <MessageCircle className="h-4 w-4" />
              </a>
              <button onClick={() => setDialog({ mode: "edit", data: { ...s } })} data-testid={`student-edit-${s.id}`} className="rounded-full border border-forest/20 p-2 text-forest hover:bg-forest hover:text-cream" aria-label="Edit student">
                <Pencil className="h-4 w-4" />
              </button>
              <button onClick={() => remove(s.id)} data-testid={`student-delete-${s.id}`} className="rounded-full border border-red-200 p-2 text-red-600 hover:bg-red-600 hover:text-white" aria-label="Delete student">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
        {students.length === 0 && (
          <p className="rounded-2xl border border-dashed border-forest/20 bg-white py-14 text-center text-sm text-stone-500" data-testid="students-empty">
            No students enrolled yet. Enrol your first student.
          </p>
        )}
      </div>

      <Dialog open={!!dialog} onOpenChange={(open) => !open && setDialog(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto bg-cream sm:max-w-lg" data-testid="student-dialog">
          {dialog && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl text-forest-deep">
                  {dialog.mode === "add" ? "Enrol New Student" : "Edit Student"}
                </DialogTitle>
              </DialogHeader>
              <div className="mt-2 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-[0.12em] text-stone-500">Student ID *</label>
                    <Input value={dialog.data.student_id} onChange={(e) => setDialog((d) => ({ ...d, data: { ...d.data, student_id: e.target.value } }))} placeholder="SMCT-2026-002" data-testid="student-form-id" className="mt-1.5 bg-white" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-[0.12em] text-stone-500">Mobile *</label>
                    <Input value={dialog.data.phone} onChange={(e) => setDialog((d) => ({ ...d, data: { ...d.data, phone: e.target.value } }))} placeholder="+91 ..." data-testid="student-form-phone" className="mt-1.5 bg-white" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.12em] text-stone-500">Full Name *</label>
                  <Input value={dialog.data.name} onChange={(e) => setDialog((d) => ({ ...d, data: { ...d.data, name: e.target.value } }))} placeholder="As on admission form" data-testid="student-form-name" className="mt-1.5 bg-white" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-[0.12em] text-stone-500">Course</label>
                    <select value={dialog.data.course} onChange={(e) => setDialog((d) => ({ ...d, data: { ...d.data, course: e.target.value } }))} data-testid="student-form-course" className={selectCls}>
                      <option value="">Select course</option>
                      {courses.map((c) => <option key={c.id} value={c.title}>{c.title}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-[0.12em] text-stone-500">Batch</label>
                    <select value={dialog.data.batch} onChange={(e) => setDialog((d) => ({ ...d, data: { ...d.data, batch: e.target.value } }))} data-testid="student-form-batch" className={selectCls}>
                      <option value="">Select batch</option>
                      {BATCHES.map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-[0.12em] text-stone-500">Status</label>
                    <select value={dialog.data.status} onChange={(e) => setDialog((d) => ({ ...d, data: { ...d.data, status: e.target.value } }))} data-testid="student-form-status" className={selectCls}>
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-[0.12em] text-stone-500">Valid Until</label>
                    <Input type="date" value={dialog.data.valid_until} onChange={(e) => setDialog((d) => ({ ...d, data: { ...d.data, valid_until: e.target.value } }))} data-testid="student-form-valid-until" className="mt-1.5 bg-white" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.12em] text-stone-500">Photo URL (optional)</label>
                  <Input value={dialog.data.photo} onChange={(e) => setDialog((d) => ({ ...d, data: { ...d.data, photo: e.target.value } }))} placeholder="https://... (blank shows initials)" data-testid="student-form-photo" className="mt-1.5 bg-white" />
                </div>
                <Button onClick={save} disabled={saving} data-testid="student-save-button" className="w-full rounded-full bg-forest py-5 font-display text-cream hover:bg-forest-deep disabled:opacity-60">
                  {saving ? "Saving..." : "Save Student"}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
